import { useId } from 'react';
import { useFormSubmit } from '../lib/forms';
import { SITE } from '../lib/site';
import { AppLink } from './AppLink';
import { FormStatusMessage } from './FormStatusMessage';
import { Honeypot } from './Honeypot';
import { Icon } from './Icon';

declare const __BUILD_YEAR__: string;

const SERVICE_LINKS = [
  { href: '/services/residential-design', label: 'Residential Interior Design' },
  { href: '/services/office-design', label: 'Office & Corporate Interiors' },
  { href: '/services/commercial-design', label: 'Commercial & Hospitality' },
  { href: '/services/modular-kitchen-design', label: 'Modular Kitchen Design' },
  { href: '/services/bespoke-joinery', label: 'Bespoke Millwork & Joinery' },
  { href: '/portfolio', label: 'Featured Case Studies' },
];

function NewsletterForm() {
  const { status, error, onSubmit } = useFormSubmit('newsletter');
  const id = useId();
  return (
    <form onSubmit={onSubmit} className="relative flex flex-col gap-space-xs" noValidate={false}>
      <Honeypot />
      <label htmlFor={`${id}-email`} className="sr-only">
        Email address
      </label>
      <div className="flex items-center bg-surface-ivory rounded px-space-sm py-space-xs shadow-[0_1px_4px_rgba(0,0,0,0.03)] focus-within:ring-1 focus-within:ring-gold-dark">
        <input
          id={`${id}-email`}
          name="email"
          type="email"
          required
          autoComplete="email"
          placeholder="Enter your private email"
          className="w-full bg-transparent font-body-sm text-on-surface placeholder:text-on-surface-variant focus:outline-none py-1"
        />
        <button
          type="submit"
          aria-label="Subscribe to the Atelier Dispatch"
          disabled={status === 'submitting'}
          className="p-space-xs text-primary hover:text-secondary transition-colors disabled:opacity-50"
        >
          <Icon name={status === 'submitting' ? 'progress_activity' : 'arrow_forward'} size={20} className={status === 'submitting' ? 'animate-spin' : ''} />
        </button>
      </div>
      <FormStatusMessage status={status} error={error} successText="Thank you — you're on the list." />
      <span className="font-label-sm text-label-sm text-on-surface-variant">Discretion assured. No marketing solicitations.</span>
    </form>
  );
}

const socialBtn =
  'w-9 h-9 rounded bg-surface-cream flex items-center justify-center text-primary hover:bg-primary hover:text-on-primary transition-colors';

export function Footer() {
  return (
    <footer className="w-full bg-surface-container-low">
      <div className="max-w-[1440px] mx-auto px-margin-mobile md:px-gutter pt-space-xl pb-space-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-gutter">
          <div className="lg:col-span-4 flex flex-col gap-space-md">
            <AppLink href="/" className="font-headline-md text-headline-md text-primary self-start">
              {SITE.shortName}
            </AppLink>
            <p className="font-body-md text-body-md text-on-surface-variant max-w-sm">
              High-end interior architecture and bespoke residential transformations crafted with organic poise and severe
              architectural precision — from our studio in Nagercoil, Tamil Nadu.
            </p>
            <div className="flex items-center gap-space-sm pt-space-xs">
              {SITE.social.instagram && (
                <a aria-label="Green Valley Interiors on Instagram" className={socialBtn} href={SITE.social.instagram} rel="noopener" target="_blank">
                  <Icon name="photo_camera" size={18} />
                </a>
              )}
              <a aria-label="Chat with us on WhatsApp" className={socialBtn} href={SITE.whatsappUrl} rel="noopener" target="_blank">
                <Icon name="chat" size={18} />
              </a>
              <a aria-label="Email the studio" className={socialBtn} href={`mailto:${SITE.email}`}>
                <Icon name="mail" size={18} />
              </a>
              <a aria-label="Find our studio on Google Maps" className={socialBtn} href={SITE.mapsUrl} rel="noopener" target="_blank">
                <Icon name="location_on" size={18} />
              </a>
            </div>
          </div>

          <div className="lg:col-span-3 flex flex-col gap-space-sm">
            <h2 className="font-label-lg text-label-lg uppercase tracking-widest text-primary">Practice &amp; Services</h2>
            <nav aria-label="Services" className="flex flex-col gap-space-xs">
              {SERVICE_LINKS.map((l) => (
                <AppLink key={l.href} href={l.href} className="font-body-sm text-body-sm text-on-surface-variant hover:text-primary transition-colors">
                  {l.label}
                </AppLink>
              ))}
            </nav>
          </div>

          <div className="lg:col-span-2 flex flex-col gap-space-sm">
            <h2 className="font-label-lg text-label-lg uppercase tracking-widest text-primary">Studio</h2>
            <address className="not-italic flex flex-col gap-space-xs">
              <span className="font-label-sm text-label-sm uppercase tracking-wider text-secondary">Tamil Nadu Headquarters</span>
              <p className="font-body-sm text-body-sm text-on-surface-variant">
                {SITE.address.line1}
                <br />
                {SITE.address.line2}, {SITE.address.locality}
                <br />
                {SITE.address.region} {SITE.address.postalCode}, {SITE.address.countryName}
              </p>
              <a className="font-body-sm text-body-sm text-primary hover:text-emerald-vivid transition-colors" href={`tel:${SITE.phoneE164}`}>
                {SITE.phoneDisplay}
              </a>
              <a className="font-body-sm text-body-sm text-primary hover:text-emerald-vivid transition-colors break-all" href={`mailto:${SITE.email}`}>
                {SITE.email}
              </a>
            </address>
          </div>

          <div className="lg:col-span-3 flex flex-col gap-space-sm">
            <h2 className="font-label-lg text-label-lg uppercase tracking-widest text-primary">Atelier Dispatch</h2>
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              Private quarterly architectural perspectives, project invitations, and material studies.
            </p>
            <NewsletterForm />
          </div>
        </div>

        <div className="mt-space-xl pt-space-md border-t border-border-muted flex flex-col sm:flex-row items-center justify-between gap-space-sm">
          <span className="font-body-sm text-body-sm text-on-surface-variant text-center sm:text-left">
            © {__BUILD_YEAR__} {SITE.name}. All rights reserved.
          </span>
          <nav aria-label="Legal" className="flex items-center gap-space-md">
            <AppLink href="/privacy-policy" className="font-body-sm text-body-sm text-on-surface-variant hover:text-primary transition-colors">
              Privacy Policy
            </AppLink>
            <AppLink href="/contact" className="font-body-sm text-body-sm text-on-surface-variant hover:text-primary transition-colors">
              Contact
            </AppLink>
            <a href="/sitemap.xml" className="font-body-sm text-body-sm text-on-surface-variant hover:text-primary transition-colors">
              Sitemap
            </a>
          </nav>
        </div>
      </div>
    </footer>
  );
}
